import { useState, useEffect, useCallback } from 'react'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import { v4 as uuidv4 } from 'uuid'
import clsx from 'clsx'
import BigNumber from 'bignumber.js'
import PercentageSlider from '@/components/PercentageSlider'
import OrderFormInput from '@/components/trade/OrderFormInput'
import OrderFormCheckbox from '@/components/trade/OrderFormCheckbox'
import RiskCalculator from '@/components/trade/RiskCalculator'
import { marketInfoValue } from '@/recoil/Market'
import { accountWalletState } from '@/recoil/Wallet'
import {
  orderTypeState,
  orderFormStateFamily,
  orderFormAutoFillQueueState,
  maxOrderSizeValue,
  orderPriceErrorValue,
  orderSizeErrorValue,
  orderTotalErrorValue,
  orderFormLimitOrderOptionState,
  orderFormIsReduceOnlyOrderState,
  orderFormIsHiddenOrderState,
  orderFormIsBestPriceOrderState
} from '@/recoil/OrderForm'
import { positionMapValue, collateralInfoValue } from '@/recoil/Position'
import { popupNotificationListState } from '@/recoil/Notification'
import { useWalletSelector } from '@/contexts/WalletSelectorContext'
import fetcher from '@/api/fetcher'
import { BROKER_ID } from '@/constants/config'
import { USDC_TOKEN_LIST } from '@/constants/token'
import {
  ORDER_SIDE,
  ORDER_TYPE_MAP,
  ORDER_TYPE_LIST,
  ORDER_INPUT_MAP
} from '@/constants/order'
import numberFormat from '@/utils/numberFormat'
import textWidth from '@/utils/textWidth'
import './OrderForm.css'

export default function OrderForm({
  id,
  orderSide = false,
  showOrderTypeSwitcher = true,
  showBalance = true,
  showOrderTypeOption = true,
  showHiddenOrderOption = true
}) {
  const { accountId, walletReady, modal } = useWalletSelector()
  const [accountWallet] = useRecoilState(accountWalletState)
  const [orderType, setOrderType] = useRecoilState(orderTypeState)
  const [orderForm, setOrderForm] = useRecoilState(orderFormStateFamily(id))
  const positionMap = useRecoilValue(positionMapValue)
  const maxOrderSize = useRecoilValue(maxOrderSizeValue(id))
  const orderPriceError = useRecoilValue(orderPriceErrorValue(id))
  const orderSizeError = useRecoilValue(orderSizeErrorValue(id))
  const orderTotalError = useRecoilValue(orderTotalErrorValue(id))
  const [orderFormAutoFillQueue] = useRecoilState(orderFormAutoFillQueueState)
  const [limitOrderOption, setLimitOrderOption] = useRecoilState(
    orderFormLimitOrderOptionState
  )
  const [isReduceOnlyOrder, setIsReduceOnlyOrder] = useRecoilState(
    orderFormIsReduceOnlyOrderState
  )
  const [isHiddenOrder, setIsHiddenOrder] = useRecoilState(
    orderFormIsHiddenOrderState
  )
  const [isBestPriceOrder, setIsBestPriceOrder] = useRecoilState(
    orderFormIsBestPriceOrderState
  )
  const setPopupNotification = useSetRecoilState(popupNotificationListState)
  const marketInfo = useRecoilValue(marketInfoValue)
  const { freeCollateral } = useRecoilValue(collateralInfoValue)
  const marketInfoReady = Object.keys(marketInfo).length > 0

  const [isLoading, setIsLoading] = useState(false)
  const [lastEditOrderInput, setLastEditOrderInput] = useState('')
  const [percentage, setPercentage] = useState(0)
  const [tokenLabelWidth, setTokenLabelWidth] = useState(0)

  const isFutures = marketInfo.isFutures
  const position = isFutures ? positionMap[marketInfo.symbol] || {} : {}
  const noPosition = !isFutures || BigNumber(position.position_qty || 0).eq(0)
  const hasBuyPosition =
    isFutures && BigNumber(position.position_qty || 0).gt(0)
  const hasSellPosition =
    isFutures && BigNumber(position.position_qty || 0).lt(0)
  const isLimitOrder = orderType === ORDER_TYPE_MAP.limit
  const isMarketOrder = orderType === ORDER_TYPE_MAP.market
  const isBuyOrder = orderForm.side === ORDER_SIDE.buy
  const currency = isBuyOrder
    ? marketInfo.quoteCurrency
    : marketInfo.baseCurrency
  const availableBalance = USDC_TOKEN_LIST.includes(currency)
    ? freeCollateral
    : accountWallet[currency]?.available || 0

  const reCalculateOrderFormValue = useCallback(
    isBestPriceOrder => {
      const data = {
        key: lastEditOrderInput,
        value: orderForm[lastEditOrderInput]
      }
      if (isBestPriceOrder !== undefined) {
        data.isBestPriceOrder = isBestPriceOrder
      }
      setOrderForm(data)
    },
    [setOrderForm, lastEditOrderInput, orderForm]
  )

  const onOrderSideUpdate = useCallback(
    value => {
      setOrderForm({ key: 'side', value })
      isMarketOrder && reCalculateOrderFormValue(isBestPriceOrder)
    },
    [setOrderForm, isMarketOrder, reCalculateOrderFormValue, isBestPriceOrder]
  )

  const onSelectOrderType = useCallback(
    value => setOrderType(value),
    [setOrderType]
  )

  const onOrderInputUpdate = useCallback(
    (type, value) => {
      setLastEditOrderInput(type)
      setOrderForm({ key: type, value, isBestPriceOrder })
    },
    [setLastEditOrderInput, setOrderForm, isBestPriceOrder]
  )

  const onSetLimitOrderOption = useCallback(
    option => {
      setLimitOrderOption(prevOption => (prevOption === option ? null : option))
    },
    [setLimitOrderOption]
  )

  const onSetIsReduceOnlyOrder = useCallback(() => {
    setIsReduceOnlyOrder(!isReduceOnlyOrder)
    reCalculateOrderFormValue()
  }, [setIsReduceOnlyOrder, isReduceOnlyOrder, reCalculateOrderFormValue])

  const onSetIsBestPriceOrder = useCallback(() => {
    setIsBestPriceOrder(!isBestPriceOrder)
    reCalculateOrderFormValue(!isBestPriceOrder)
  }, [setIsBestPriceOrder, isBestPriceOrder, reCalculateOrderFormValue])

  const getOrderType = useCallback(() => {
    if (isLimitOrder) {
      if (limitOrderOption) {
        return limitOrderOption
      }
      return ORDER_TYPE_MAP.limit
    }

    if (isMarketOrder) {
      if (isBestPriceOrder) {
        return orderForm.side === ORDER_SIDE.buy
          ? ORDER_TYPE_MAP.bestAsk
          : ORDER_TYPE_MAP.bestBid
      }
      return ORDER_TYPE_MAP.market
    }
  }, [
    isLimitOrder,
    isMarketOrder,
    orderForm,
    limitOrderOption,
    isBestPriceOrder
  ])

  const onPercentageChange = percentage => {
    const balance = isFutures ? maxOrderSize : availableBalance
    const isSpotBuyOrder = !isFutures && isBuyOrder
    const type = isSpotBuyOrder ? ORDER_INPUT_MAP.total : ORDER_INPUT_MAP.size
    let value = BigNumber(balance).times(percentage).div(100)
    value = isSpotBuyOrder
      ? value.toFixed()
      : value.dp(marketInfo.sizeDecimal, BigNumber.ROUND_DOWN).toFixed()
    value = BigNumber(value).eq(0) ? '' : value
    setPercentage(percentage)
    onOrderInputUpdate(type, value)
  }

  const onSubmitOrder = useCallback(
    orderNotValid => {
      if (orderNotValid) {
        return
      }
      setIsLoading(true)

      const { side, price, size, total } = orderForm
      const data = {
        client_order_id: uuidv4().replaceAll('-', '_'),
        broker_id: BROKER_ID,
        symbol: marketInfo.symbol,
        order_type: getOrderType(),
        side
      }

      if (
        isReduceOnlyOrder &&
        ((hasBuyPosition && side === ORDER_SIDE.sell) ||
          (hasSellPosition && side === ORDER_SIDE.buy))
      ) {
        data.reduce_only = isReduceOnlyOrder
      }

      if (orderType === ORDER_TYPE_MAP.limit) {
        data.order_price = Number(price)
        data.order_quantity = Number(size)
        if (isHiddenOrder) {
          data.visible_quantity = 0
        }
      }

      if (orderType === ORDER_TYPE_MAP.market) {
        if (
          side === ORDER_SIDE.sell ||
          lastEditOrderInput === ORDER_INPUT_MAP.size
        ) {
          data.order_quantity = Number(size)
        } else {
          data.order_amount = Number(total)
        }
      }

      console.log(data)

      return fetcher
        .createOrder(data)
        .then(() =>
          setOrderForm({
            resetConfig: { side: orderSide || side },
            isReset: true
          })
        )
        .catch(e => {
          const errorCode = e.response?.data?.code
          const errorMessage = e.response?.data?.message
          errorMessage &&
            setPopupNotification({
              notification: {
                key: Date.now(),
                status: 'error',
                code: errorCode,
                message: errorMessage
              }
            })
        })
        .finally(() => setIsLoading(false))
    },
    [
      marketInfo.symbol,
      hasBuyPosition,
      hasSellPosition,
      orderType,
      orderSide,
      getOrderType,
      orderForm,
      setOrderForm,
      isReduceOnlyOrder,
      isHiddenOrder,
      lastEditOrderInput,
      setPopupNotification
    ]
  )

  useEffect(() => {
    setOrderForm({ key: 'type', orderType })
    setPercentage(0)
  }, [orderType, setOrderForm])

  useEffect(() => {
    orderSide &&
      setOrderForm({
        resetConfig: {
          side: orderSide
        },
        isReset: true
      })
  }, [orderSide, setOrderForm])

  useEffect(() => {
    if (!marketInfoReady) {
      return
    }
    setTokenLabelWidth(
      Math.max(
        textWidth(marketInfo.baseCurrency),
        textWidth(marketInfo.quoteCurrency)
      )
    )
    setOrderForm({
      resetConfig: orderSide ? { side: orderSide || orderForm.side } : {},
      isReset: true
    })
  }, [orderSide, marketInfoReady, marketInfo, orderForm.side, setOrderForm])

  useEffect(() => {
    orderFormAutoFillQueue.forEach(({ key, value }) =>
      onOrderInputUpdate(key, value)
    )
  }, [orderFormAutoFillQueue, onOrderInputUpdate])

  useEffect(() => {
    if (!isFutures && isBuyOrder) {
      return
    }
    const balance = isFutures ? maxOrderSize : availableBalance
    const percentage = BigNumber(orderForm.size || 0)
      .div(balance)
      .times(100)
    percentage.isFinite() && setPercentage(Math.min(100, percentage.toNumber()))
  }, [isFutures, isBuyOrder, maxOrderSize, availableBalance, orderForm.size])

  useEffect(() => {
    if (isFutures || !isBuyOrder) {
      return
    }
    const percentage = BigNumber(orderForm.total || 0)
      .div(availableBalance)
      .times(100)
    percentage.isFinite() && setPercentage(Math.min(100, percentage.toNumber()))
  }, [isFutures, isBuyOrder, availableBalance, orderForm.total])

  const OrderTypeOptionList = ORDER_TYPE_LIST.map(type => (
    <div
      key={type}
      className={clsx('order-type', {
        active: type === orderType
      })}
      onClick={() => type !== orderType && onSelectOrderType(type)}
    >
      {type.toLowerCase()}
    </div>
  ))

  const isPostOnly = limitOrderOption === ORDER_TYPE_MAP.postOnly
  const isIoC = limitOrderOption === ORDER_TYPE_MAP.immediateOrCancel
  const isFoK = limitOrderOption === ORDER_TYPE_MAP.fillOrKill

  const orderPriceEmpty = orderForm.price === ''
  const orderSizeEmpty = orderForm.size === ''
  const orderTotalEmpty = orderForm.total === ''
  const inputHasError = orderPriceError || orderSizeError || orderTotalError
  const limitOrderFormNotValid = orderPriceEmpty || orderSizeEmpty
  const marketOrderFormNotValid =
    lastEditOrderInput === ORDER_INPUT_MAP.size
      ? orderSizeEmpty
      : orderTotalEmpty
  const orderFormEmptyCheck = isLimitOrder
    ? limitOrderFormNotValid
    : marketOrderFormNotValid
  const submitButtonDisabled =
    isLoading ||
    !walletReady ||
    !marketInfoReady ||
    orderFormEmptyCheck ||
    (isReduceOnlyOrder && isBuyOrder && hasBuyPosition) ||
    (isReduceOnlyOrder && !isBuyOrder && hasSellPosition) ||
    inputHasError

  return (
    <div
      className={clsx('noselect order-form', {
        'single-side-mode': orderSide,
        'add-padding-top': !showOrderTypeSwitcher && !showBalance
      })}
    >
      {!orderSide && (
        <div className="order-side-switcher">
          <div
            className={clsx('order-side order-buy', {
              active: isBuyOrder
            })}
            onClick={() => onOrderSideUpdate(ORDER_SIDE.buy)}
          >
            {ORDER_SIDE.buy}
          </div>
          <div
            className={clsx('order-side order-sell', {
              active: !isBuyOrder
            })}
            onClick={() => onOrderSideUpdate(ORDER_SIDE.sell)}
          >
            {ORDER_SIDE.sell}
          </div>
        </div>
      )}
      {showOrderTypeSwitcher && (
        <div className="order-type-list">{OrderTypeOptionList}</div>
      )}
      {showBalance && (
        <div className="available-balance-info">
          {marketInfoReady && walletReady && (
            <>
              Available
              <div className="available-balance">
                {numberFormat(availableBalance, 'max2')}
              </div>
              {currency}
            </>
          )}
        </div>
      )}
      {isLimitOrder && (
        <OrderFormInput
          label={ORDER_INPUT_MAP.price}
          unitLabel={marketInfo.quoteCurrency}
          unitLabelWidth={tokenLabelWidth}
          value={orderForm.price}
          error={accountId ? orderPriceError : ''}
          onUpdate={onOrderInputUpdate}
        />
      )}
      <OrderFormInput
        label={ORDER_INPUT_MAP.size}
        unitLabel={marketInfo.baseCurrency}
        unitLabelWidth={tokenLabelWidth}
        value={orderForm.size}
        error={accountId ? orderSizeError : ''}
        isEstimate={orderForm.estimatedValue === ORDER_INPUT_MAP.size}
        onUpdate={onOrderInputUpdate}
      />
      <PercentageSlider
        value={percentage}
        showLabel={true}
        onUpdate={onPercentageChange}
        showValueLabelOnDrag={true}
      />
      <OrderFormInput
        className="last-order-form-input"
        label={ORDER_INPUT_MAP.total}
        unitLabel={marketInfo.quoteCurrency}
        unitLabelWidth={tokenLabelWidth}
        value={orderForm.total}
        error={accountId ? orderTotalError : ''}
        isEstimate={orderForm.estimatedValue === ORDER_INPUT_MAP.total}
        onUpdate={onOrderInputUpdate}
      />
      {isFutures && (
        <RiskCalculator
          id={id}
          orderSide={orderForm.side}
          orderPrice={orderForm.price}
          orderSize={orderForm.size}
        />
      )}
      {isLimitOrder && (
        <>
          {showOrderTypeOption && (
            <div className="checkbox-wrap">
              <OrderFormCheckbox
                label="Post Only"
                value={isPostOnly}
                onUpdate={() => onSetLimitOrderOption(ORDER_TYPE_MAP.postOnly)}
              />
              <OrderFormCheckbox
                label="IOC"
                value={isIoC}
                onUpdate={() =>
                  onSetLimitOrderOption(ORDER_TYPE_MAP.immediateOrCancel)
                }
              />
              <OrderFormCheckbox
                label="FOK"
                value={isFoK}
                onUpdate={() =>
                  onSetLimitOrderOption(ORDER_TYPE_MAP.fillOrKill)
                }
              />
            </div>
          )}
          {showOrderTypeOption && showHiddenOrderOption && (
            <div className="order-form-divider" />
          )}
          {showHiddenOrderOption && (
            <div className="checkbox-wrap">
              <OrderFormCheckbox
                label="Hidden"
                value={isHiddenOrder}
                onUpdate={() => setIsHiddenOrder(prevValue => !prevValue)}
              />
              {isFutures && (
                <OrderFormCheckbox
                  label="Reduce Only"
                  value={!noPosition && isReduceOnlyOrder}
                  disabled={noPosition}
                  onUpdate={onSetIsReduceOnlyOrder}
                />
              )}
            </div>
          )}
        </>
      )}
      {isMarketOrder && (
        <div className="checkbox-wrap">
          {showOrderTypeOption && (
            <>
              <OrderFormCheckbox
                label="Best Price"
                value={isBestPriceOrder}
                onUpdate={onSetIsBestPriceOrder}
              />
              {isFutures && (
                <OrderFormCheckbox
                  label="Reduce Only"
                  value={!noPosition && isReduceOnlyOrder}
                  disabled={noPosition}
                  onUpdate={onSetIsReduceOnlyOrder}
                />
              )}
            </>
          )}
        </div>
      )}
      <div className="empty-fill-box" />
      <button
        className={clsx('order-form-submit-button', {
          'order-buy': isBuyOrder,
          'order-sell': !isBuyOrder,
          disabled: accountId && submitButtonDisabled,
          'connect-wallet': !accountId
        })}
        onClick={() =>
          !accountId ? modal.show() : onSubmitOrder(submitButtonDisabled)
        }
      >
        {accountId ? orderForm.side : 'Connect Wallet'}
      </button>
    </div>
  )
}
